import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import type { ItemAlmox, Row } from "../types";
import { IconClose, IconCheck, IconSearch, IconTruck } from "../icons";

interface Props {
  atividade: Row;
  onClose: () => void;
  onPedida: () => void;
}

/** Quantidade escolhida por insumo, indexada pelo id do item no Almoxarifado. */
type Escolhidos = Record<number, string>;

/**
 * Pedido de separação ao Almoxarifado, a partir da OS planejada.
 *
 * O carrinho aceita VÁRIOS insumos de uma vez porque é assim que a OS é: uma
 * lista do que a equipe leva. No Almoxarifado, porém, cada separação é de um
 * item só — o servidor daqui quebra a lista em vários pedidos e amarra todos
 * pelo número da OS no texto (ver server/almoxarifado.ts).
 */
export default function PedirSeparacaoModal({ atividade, onClose, onPedida }: Props) {
  const [itens, setItens] = useState<ItemAlmox[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [escolhidos, setEscolhidos] = useState<Escolhidos>({});
  const [prioridade, setPrioridade] = useState("Normal");
  const [observacao, setObservacao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api.separacao
      .itens()
      .then((lista) => {
        if (vivo) setItens(lista);
      })
      .catch((err) => {
        if (vivo) setErroCarga(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const base = q
      ? itens.filter((i) =>
          [i.codigo, i.descricao, i.categoria, i.localizacao]
            .map((v) => String(v ?? "").toLowerCase())
            .some((v) => v.includes(q)),
        )
      : itens;
    // Sem busca a lista inteira do estoque não cabe na tela — e rolar 400 itens
    // procurando um parafuso é pior que digitar três letras.
    return q ? base.slice(0, 80) : base.slice(0, 40);
  }, [itens, busca]);

  /** Itens com quantidade > 0, prontos para virar pedido. */
  const selecionados = useMemo(
    () =>
      Object.entries(escolhidos)
        .map(([id, qtd]) => ({ item_id: Number(id), quantidade: Number(qtd) }))
        .filter((e) => Number.isFinite(e.quantidade) && e.quantidade > 0),
    [escolhidos],
  );

  function setQtd(id: number, valor: string) {
    setEscolhidos((atuais) => ({ ...atuais, [id]: valor }));
  }

  async function enviar() {
    if (selecionados.length === 0) {
      setErro("Informe a quantidade de ao menos um insumo.");
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const r = await api.separacao.pedir(atividade.id, {
        itens: selecionados,
        prioridade,
        os_numero: String(atividade.codigo_os ?? ""),
        cliente: String(atividade.cliente ?? ""),
        atividade: String(atividade.nome ?? ""),
        observacao: observacao.trim(),
      });
      const parcial = r.falhas?.length
        ? ` ${r.falhas.length} insumo(s) não entraram — confira o estoque no Almoxarifado.`
        : "";
      setOk(
        `${r.criadas} pedido(s) de separação abertos no Almoxarifado.${parcial} ` +
          "O almoxarife vê tudo na aba Solicitações.",
      );
      onPedida();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Falha ao enviar o pedido.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-wide" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconTruck size={18} /> Separação de material
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="sep-resumo">
            <strong>
              {atividade.codigo_os ? `OS ${atividade.codigo_os}` : "Atividade sem OS"}
              {atividade.cliente ? ` · ${atividade.cliente}` : ""}
            </strong>
            <span>{String(atividade.nome ?? "—")}</span>
          </div>

          {erroCarga ? (
            <div className="form-error">{erroCarga}</div>
          ) : (
            <>
              <div className="search-wrap sep-busca">
                <IconSearch size={17} />
                <input
                  className="search"
                  placeholder="Buscar insumo por código, descrição ou categoria..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>

              <div className="sep-lista">
                {carregando ? (
                  <div className="empty">Carregando o estoque...</div>
                ) : filtrados.length === 0 ? (
                  <div className="empty">Nenhum insumo encontrado.</div>
                ) : (
                  filtrados.map((i) => {
                    const qtd = escolhidos[i.id] ?? "";
                    const excede = Number(qtd) > i.saldo;
                    return (
                      <div key={i.id} className={`sep-item${qtd ? " sep-item-ativo" : ""}`}>
                        <div className="sep-item-info">
                          <strong>{i.descricao ?? "(sem descrição)"}</strong>
                          <span>
                            {i.codigo ?? "—"} · saldo {i.saldo} {i.unidade ?? ""}
                            {i.localizacao ? ` · ${i.localizacao}` : ""}
                          </span>
                        </div>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          className={`sep-item-qtd${excede ? " sep-item-qtd-alerta" : ""}`}
                          placeholder="qtd"
                          value={qtd}
                          onChange={(e) => setQtd(i.id, e.target.value)}
                        />
                      </div>
                    );
                  })
                )}
              </div>

              <div className="form-grid">
                <label className="field">
                  <span>Prioridade</span>
                  <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
                    <option value="Normal">Normal</option>
                    <option value="Alta">Alta</option>
                    <option value="Urgente">Urgente</option>
                  </select>
                </label>
                <label className="field field-wide">
                  <span>Observação para o almoxarife (opcional)</span>
                  <textarea
                    rows={2}
                    value={observacao}
                    placeholder="Ex.: separar até quinta, equipe sai 6h..."
                    onChange={(e) => setObservacao(e.target.value)}
                  />
                </label>
              </div>

              {selecionados.length > 0 && (
                <p className="sep-nota">
                  {selecionados.length} insumo(s) selecionado(s) — cada um vira um pedido no
                  Almoxarifado, todos amarrados a esta OS.
                </p>
              )}
            </>
          )}

          {erro && <div className="form-error">{erro}</div>}
          {ok && (
            <div className="form-ok">
              <IconCheck size={16} /> {ok}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {ok ? "Fechar" : "Cancelar"}
          </button>
          {!ok && !erroCarga && (
            <button type="button" className="btn-primary" onClick={enviar} disabled={enviando}>
              {enviando ? "Enviando..." : "Pedir separação"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
